import { useCallback, useState, useRef, ChangeEvent } from 'react';
import Hammer from 'react-hammerjs';
import AvatarEditor from 'react-avatar-editor';
import clamp from 'lodash/clamp';

import Range from 'lib/form/range/range';
import { AnyObject } from 'webclient.constants';
import { RangeContainer } from 'lib/form/range/range.style';
import * as Styled from './image-picker.style';
import {
  ImagePickerMode,
  Props,
  ImageState,
  THUMBNAIL_WIDTH,
  THUMBNAIL_HEIGHT,
  DEFAULT_EDITOR_PROPS,
  CropData,
} from './image-picker.constants';
import RoundButton from 'approot/shared/round-icon-button/round-icon-button';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import HeroPreview from 'approot/shared/hero/hero-preview';
import type { ImageCropJsonInput } from '../../../gql/schema-types';
import FileDropperLoading from 'lib/form/file-dropper/file-dropper-loading';
import { LabelStyled } from 'approot/shared/styles/inputs.style';
import IconTrash from 'approot/shared/icons/icon-trash';

const randomNumber = (Math.random() * (9999 - 0 + 1)) << 0;

export const DEFAULT_CROP_DATA: ImageCropJsonInput = {
  canvasData: {
    naturalWidth: 0,
    naturalHeight: 0,
  },
  cropData: {
    x: 0.5,
    y: 0.5,
    width: 0,
    height: 0,
  },
  zoom: 1,
};

type PickerState = {
  pickerWidthPixels: number;
  pickerHeightPixels: number;
  imageReady: boolean;
};

const ImagePicker = ({
  className,
  label,
  cropValue,
  images,
  onCropChange,
  name,
  url,
  title,
  cardDetail,
  cardMetaIcon,
  cardMetaLine,
  cardPillIcon,
  cardPillText,
  contentColour,
  imageRatio,
  isDisabled,
  deleteImage,
  imagePickerActions,
  mode,
}: Props) => {
  const RANGE_MIN = 1; // arbitrary value
  const RANGE_MAX = 5; // arbitrary value
  const RANGE_STEP = 0.1;
  const crop = cropValue;
  const [pickerState, setPickerState] = useState<PickerState>({
    pickerWidthPixels: 0,
    pickerHeightPixels: 0,
    imageReady: false,
  });
  const [imageState, setImageState] = useState<ImageState>({
    imageWidth: 0,
    imageHeight: 0,
    x:
      crop && crop.cropData && crop.cropData.x
        ? crop.cropData.x
        : DEFAULT_CROP_DATA.cropData.x,
    y:
      crop && crop.cropData && crop.cropData.y
        ? crop.cropData.y
        : DEFAULT_CROP_DATA.cropData.y,
    zoom: crop
      ? clamp(crop.zoom, RANGE_MIN, RANGE_MAX)
      : DEFAULT_CROP_DATA.zoom,
  });

  const editor = useRef<AvatarEditor | undefined>(undefined);

  const onImageReady = useCallback(() => {
    // for the hero mode we consider the image being ready only once the picker has been resized.
    // see setPickerSize
    if (mode !== ImagePickerMode.Hero) {
      setPickerState({
        imageReady: true,
        pickerWidthPixels: THUMBNAIL_WIDTH,
        pickerHeightPixels: imageRatio
          ? (imageRatio / 100) * THUMBNAIL_WIDTH
          : THUMBNAIL_HEIGHT,
      });
    }
  }, [mode, imageRatio]);

  const sendNewCropBox = useCallback(
    (
      imageWidth: number,
      imageHeight: number,
      x: number,
      y: number,
      zoom: number
    ) => {
      const newCropBox: CropData = {
        canvasData: {
          naturalWidth: imageWidth,
          naturalHeight: imageHeight,
        },
        cropData: {
          x: x,
          y: y,
          width: pickerState.pickerWidthPixels,
          height: pickerState.pickerHeightPixels,
        },
        zoom,
      };
      onCropChange(newCropBox);
    },
    [
      pickerState.pickerHeightPixels,
      pickerState.pickerWidthPixels,
      onCropChange,
    ]
  );

  const updateZoom = useCallback(
    (newZoom: number) => {
      if (imageState.zoom !== newZoom) {
        setImageState({
          ...imageState,
          zoom: newZoom,
        });
        sendNewCropBox(
          imageState.imageWidth,
          imageState.imageHeight,
          imageState.x,
          imageState.y,
          newZoom
        );
      }
    },
    [imageState, sendNewCropBox]
  );

  const onLoadSuccess = useCallback(
    (imgInfo: AnyObject) => {
      if (!pickerState.imageReady || !imgInfo.resource) return;

      let zoom: number = 1;
      let newX: number = imageState.x;
      let newY: number = imageState.y;

      if (cropValue) {
        if (cropValue.zoom) {
          zoom = cropValue.zoom;
        }

        const { cropData } = cropValue;
        if (cropData) {
          const { x, y } = cropData;
          if (x && y) {
            newX = x;
            newY = y;
          }
        }
      }
      const newState: ImageState = {
        imageWidth: imgInfo.resource.width,
        imageHeight: imgInfo.resource.height,
        x: newX,
        y: newY,
        zoom: clamp(zoom, RANGE_MIN, RANGE_MAX),
      };

      setImageState(newState);

      // In case the user doesn't change anything and press save directly
      // x and y will be undefined and will need to be saved as 0.5
      let { imageWidth, imageHeight, x, y } = newState;
      if ((!x && !y) || (x === 0.5 && y === 0.5)) {
        if (!x) x = 0.5;
        if (!y) y = 0.5;
        zoom = newState.zoom;

        sendNewCropBox(imageWidth, imageHeight, x, y, zoom);
      }
    },
    [pickerState, cropValue, imageState, sendNewCropBox]
  );

  const onImageChange = useCallback(() => {
    if (
      pickerState.imageReady &&
      editor &&
      editor.current &&
      editor.current.state &&
      editor.current.state.image
    ) {
      const { x: editorX, y: editorY } = editor.current.state.image;
      const stateX = imageState.x;
      const stateY = imageState.y;

      // We don't want to refresh if values are the same or if === 0.5 (cf comment below)
      if (
        (editorX !== stateX || editorY !== stateY) &&
        (editorX !== 0.5 || editorY !== 0.5)
      ) {
        setImageState({
          ...imageState,
          x: editorX,
          y: editorY,
        });

        const { imageWidth, imageHeight, zoom } = imageState;
        sendNewCropBox(imageWidth, imageHeight, editorX, editorY, zoom);
      }
    }
  }, [imageState, pickerState, sendNewCropBox]);

  const handleRangeChange = useCallback(
    (changeEvent: ChangeEvent<HTMLInputElement>) => {
      const zoom = parseFloat(changeEvent.target.value);
      updateZoom(zoom);
    },
    [updateZoom]
  );

  const zoomOut = useCallback(() => {
    const newZoom = clamp(imageState.zoom - RANGE_STEP, RANGE_MIN, RANGE_MAX);
    updateZoom(newZoom);
  }, [imageState.zoom, updateZoom]);

  const zoomIn = useCallback(() => {
    const newZoom = clamp(imageState.zoom + RANGE_STEP, RANGE_MIN, RANGE_MAX);
    updateZoom(newZoom);
  }, [imageState.zoom, updateZoom]);

  const setEditorRef = useCallback((avatarEditor: AvatarEditor) => {
    editor.current = avatarEditor;
  }, []);

  const setPickerSize = useCallback(
    (width: number, height: number) => {
      setPickerState({
        pickerWidthPixels: width,
        pickerHeightPixels: height,
        imageReady: true,
      });
    },
    [setPickerState]
  );

  // requires to use style prop as classname isn't supported by plugin
  const getEditorStyle = useCallback(
    (mode: string) => {
      const textAlign = !pickerState.imageReady ? 'center' : 'left';
      return {
        borderRadius: mode === ImagePickerMode.Hero ? '' : '5px',
        margin: '0px auto',
        cursor: 'move',
        display:
          mode === ImagePickerMode.Editor && !pickerState.imageReady
            ? 'none'
            : 'block',
        textAlign,
      };
    },
    [pickerState.imageReady]
  );

  const renderDeleteButton = useCallback(() => {
    return (
      <Styled.ImageDeleteButtonWrapper mode={mode}>
        <TooltipWrapper hoverText="Remove Image">
          <RoundButton
            noBorder
            data-selector="delete-image"
            aria-label="Remove Image"
            icon={<IconTrash width={20} height={20} />}
            onClick={deleteImage}
          />
        </TooltipWrapper>
      </Styled.ImageDeleteButtonWrapper>
    );
  }, [mode, deleteImage]);

  const renderEditor = useCallback(
    (showDelete?: boolean) => {
      const editorStyle = getEditorStyle('editor');
      //by default pinch is disabled for react-hammer
      const hammerOptions = {
        recognizers: {
          pinch: { enable: true },
        },
      };

      return (
        <Hammer options={hammerOptions} onPinchIn={zoomOut} onPinchOut={zoomIn}>
          <Styled.ImagePickerAvatarEditor
            mode={mode}
            data-selector="square-image-container"
          >
            {showDelete &&
              deleteImage &&
              pickerState.imageReady &&
              renderDeleteButton()}
            <AvatarEditor
              {...DEFAULT_EDITOR_PROPS}
              ref={setEditorRef}
              key={url}
              image={url}
              position={{ x: imageState.x, y: imageState.y }}
              width={pickerState.pickerWidthPixels}
              height={pickerState.pickerHeightPixels}
              scale={imageState.zoom}
              style={editorStyle}
              onPositionChange={onImageChange}
              onImageChange={onImageChange}
              onLoadSuccess={onLoadSuccess}
              onImageReady={onImageReady}
            />
          </Styled.ImagePickerAvatarEditor>
        </Hammer>
      );
    },
    [
      getEditorStyle,
      imageState,
      mode,
      onImageChange,
      onImageReady,
      onLoadSuccess,
      pickerState,
      setEditorRef,
      url,
      zoomIn,
      zoomOut,
      deleteImage,
      renderDeleteButton,
    ]
  );

  const renderHeroImage = useCallback(() => {
    return (
      <>
        {!pickerState.imageReady && <FileDropperLoading />}
        {renderEditor(true)}
        <HeroPreview
          url={url}
          name={name}
          rounded={true}
          images={images || []}
          onImageLoaded={setPickerSize}
          isEditing={true}
        />
      </>
    );
  }, [renderEditor, images, name, setPickerSize, url, pickerState]);

  const renderSlider = useCallback(() => {
    return (
      <RangeContainer>
        <Range
          id={`range-slider-${randomNumber}`}
          handleChange={handleRangeChange}
          value={isNaN(imageState.zoom) ? RANGE_MIN : imageState.zoom}
          min={RANGE_MIN}
          max={RANGE_MAX}
          isDisabled={isDisabled}
          step={RANGE_STEP}
          onDecrement={zoomOut}
          onIncrement={zoomIn}
        />
        <LabelStyled htmlFor={`range-slider-${randomNumber}`}>
          Drag the image to reposition it and use the slider to scale.
        </LabelStyled>
      </RangeContainer>
    );
  }, [isDisabled, handleRangeChange, imageState.zoom, zoomIn, zoomOut]);

  const renderContentCard = useCallback(() => {
    const editor = (
      <>
        {deleteImage &&
          pickerState.imageReady &&
          !isDisabled &&
          renderDeleteButton()}
        <Styled.ImagePickerCardWrapper>
          {renderEditor()}
        </Styled.ImagePickerCardWrapper>
      </>
    );

    return (
      <Styled.ImagePickerContentCard
        disabled
        isEditable={false}
        title={title || ''}
        detail={cardDetail}
        pillText={cardPillText}
        pillIcon={cardPillIcon}
        imageEditor={isDisabled ? undefined : editor}
        contentColour={contentColour}
        metaLine={cardMetaLine}
        imageRatio={imageRatio}
        ignoreImageOrientation
        images={images || []}
        metaLineIcon={cardMetaIcon}
      />
    );
  }, [
    cardDetail,
    cardMetaIcon,
    cardMetaLine,
    cardPillIcon,
    cardPillText,
    contentColour,
    imageRatio,
    images,
    isDisabled,
    renderEditor,
    title,
    deleteImage,
    pickerState.imageReady,
    renderDeleteButton,
  ]);

  return (
    <div className={className}>
      {label}

      <Styled.ImagePickerContainer>
        {mode === ImagePickerMode.Hero
          ? renderHeroImage()
          : renderContentCard()}
        {isDisabled && <Styled.ImagePickerDisabledOverlay />}
      </Styled.ImagePickerContainer>
      {(pickerState.imageReady || isDisabled) && renderSlider()}
      {pickerState.imageReady && imagePickerActions ? imagePickerActions : null}
    </div>
  );
};

export default ImagePicker;
